import { call, put, takeLatest } from "redux-saga/effects";
import apiRequest, { authorizedRequest } from "utils/api";
import { ToastError } from "components/Global/Toasts/Toasty";
import { createSelector } from "reselect";
import { trackLogin, trackLogout } from "lib/mixpanel";
import jwtDecode from "jwt-decode";
import { getSessionCookie } from "utils/sessionCookie";
import { getEnvConfig } from "helpers/environment-config";
import { buildLoginPath } from "helpers/auth-redirect";

import {
  defaultReturn,
  makePayload,
  generateDefaultActions,
  requestSaga
} from "../utils/helpers";

import {
  GET_COMPANY_LEVEL_BY_ID,
  GET_COMPANY_TYPE_BY_ID,
  getCompanyLevelById,
  getCompanyTypeById,
  getCompanyLevelByIdSaga,
  getCompanyTypeByIdSaga
} from "./companies";

import * as configItemsIds from "constants/configItemParents";

export const AUTH_LOGIN_REQUEST = "AUTH_LOGIN_REQUEST";
export const AUTH_LOGIN_SUCCESS = "AUTH_LOGIN_SUCCESS";
export const AUTH_LOGIN_FAILURE = "AUTH_LOGIN_FAILURE";
export const AUTH_LOGIN_CHECK_TOKEN = "AUTH_LOGIN_CHECK_TOKEN";
export const AUTH_LOGIN_CHECK_FAILURE = "AUTH_LOGIN_CHECK_FAILURE";
export const AUTH_LOGOUT_REQUEST = "AUTH_LOGOUT_REQUEST";
export const AUTH_LOGOUT_SUCCESS = "AUTH_LOGOUT_SUCCESS";
export const AUTH_LOGOUT_FAILURE = "AUTH_LOGOUT_FAILURE";
export const REQUEST_SISENSE_JWT = generateDefaultActions("REQUEST_SISENSE_JWT");
const AUTH_IS_APPS_ADMIN = generateDefaultActions("AUTH_IS_APPS_ADMIN");

const initialState = {
  authed: false,
  user: null,
  error: null,
  loaded: false,
  isSystemAdmin: false,
  isCompanyAdmin: false,
  companyLevel: "",
  companyTypes: [],
  isGuest: false,
  guestAccess: {},
  sisenseJWT: null
};

// Reducer
export default function authReducer(state = initialState, { type, payload }) {
  switch (type) {
    case AUTH_LOGIN_SUCCESS:
      document.cookie = `user_id=${payload.user.id};`;
      document.cookie = `user_role=${payload.user.role};`;
      document.cookie = `company_id=${payload.user.contact.companyId};`;

      return {
        ...state,
        user: payload.user,
        sisenseJWT: payload.sisenseJWT,
        authed: true,
        loaded: true,
        isSystemAdmin: payload.user.roles.includes("systemAdmin"),
        isCompanyAdmin: payload.user.roles.includes("companyAdmin"),
        isGuest: payload.user.roles.includes("guest"),
        guestAccess: payload.guestAccess
      };

    case AUTH_IS_APPS_ADMIN.success:
      return {
        ...state,
        isAppsAdmin: payload.data
      };

    case AUTH_IS_APPS_ADMIN.failure:
      ToastError("Unable to check if user is an apps admin");

      return {
        ...state,
        isAppsAdmin: undefined
      };

    case AUTH_LOGOUT_SUCCESS:
      return {
        authed: false,
        sisenseJWT: null,
        user: null,
        isSystemAdmin: false,
        isCompanyAdmin: false
      };

    case AUTH_LOGIN_FAILURE:
      if (payload.error.includes("403")) {
        ToastError("Invalid Login");
      }

      return {
        ...state,
        error: payload.error,
        loaded: true
      };
    case AUTH_LOGIN_CHECK_FAILURE:
      return {
        ...state,
        loaded: true
      };
    case AUTH_SISENSE_LOGIN.success:
      return payload;

    case GET_COMPANY_LEVEL_BY_ID.success:
      return defaultReturn(state, payload.level, "companyLevel");

    case GET_COMPANY_TYPE_BY_ID.success:
      return defaultReturn(state, payload.companyTypes, "companyTypes");

    case REQUEST_SISENSE_JWT.success:
      return defaultReturn(state, payload.data, "sisenseJWT");

    default:
      return state;
  }
}

// Action

export const requestSisenseJWT = payload => makePayload(REQUEST_SISENSE_JWT.value, payload);
function* requestSisenseJWTSaga() {
  const url = "/sisense/getJWT";
  yield requestSaga(REQUEST_SISENSE_JWT, url);
}

export function loginRequest(payload) {
  return {
    type: AUTH_LOGIN_REQUEST,
    payload
  };
}

export function isAppsAdmin(payload) {
  return {
    type: AUTH_IS_APPS_ADMIN.value,
    payload
  };
}

export function checkAuth() {
  return {
    type: AUTH_LOGIN_CHECK_TOKEN
  };
}

export function loginSuccess(payload) {
  return {
    type: AUTH_LOGIN_SUCCESS,
    payload
  };
}

export function loginFailure(error) {
  return {
    type: AUTH_LOGIN_FAILURE,
    payload: { error }
  };
}

export function logoutRequest(payload) {
  return {
    type: AUTH_LOGOUT_REQUEST,
    payload
  };
}
export function logoutSuccess(payload) {
  return {
    type: AUTH_LOGOUT_SUCCESS,
    payload
  };
}
export function logoutFailure(payload) {
  return {
    type: AUTH_LOGOUT_FAILURE,
    payload
  };
}
export function loginFailureCheckToken(payload) {
  return {
    type: AUTH_LOGIN_CHECK_FAILURE,
    payload
  };
}

// Saga
function* login(action) {
  try {
    const { payload } = action;
    const response = yield call(apiRequest, "/login", "POST", payload, {}, true);
    localStorage.setItem("pensford", response.user.id);
    localStorage.setItem("contactId", response.user.contact ? response.user.contact.id : null);
    localStorage.setItem("sisenseJWT", response.sisenseJWT);
    localStorage.setItem("contactSettings", JSON.stringify(response.contactSettings));
    yield put(getCompanyLevelById({ id: response.user.contact.companyId }));
    yield put(getCompanyTypeById({ companyId: response.user.contact.companyId }));
    yield put(loginSuccess(response));
    trackLogin(response.user.id);
  } catch (error) {
    yield put(loginFailure(error.message));
  }
}

function* isAppsAdminSaga() {
  try {
    const response = yield call(authorizedRequest, "/appsSettings/canView");
    yield put(AUTH_IS_APPS_ADMIN.createSuccess(response));

    return { error: false };
  } catch (error) {
    yield put(AUTH_IS_APPS_ADMIN.createFailure(error));

    return { error: true };
  }
}

const checkToken = function* () {
  const { pathname } = window.location;
  try {
    if (pathname === "/logout") {
      return;
    }
    const token = yield call(getSessionCookie);
    if (!token) {
      throw new Error("No token found.");
    }
    const parsedToken = jwtDecode(token);
    const userId = parsedToken.user;

    localStorage.setItem("pensford", userId);
    const response = yield call(authorizedRequest, `/users/${userId}`);

    yield put(loginSuccess(response));
    yield put(getCompanyLevelById({ id: response.user.contact.companyId }));
    yield put(getCompanyTypeById({ companyId: response.user.contact.companyId }));
    trackLogin(response.user.id);
  } catch (error) {
    // toast(error.message);
    yield put(loginFailureCheckToken(error.message));
  }
};

const logout = function* (action) {
  const { backTo } = action.payload ?? {};

  yield call(authorizedRequest, "/logout", "DELETE");
  yield put(logoutSuccess());

  // remove all local storage items except `viewConfiguration.BrokerDealsDashboard.storeVersion`, `viewConfiguration.BrokerDealsDashboard.columns`,  `viewConfiguration.BrokerDealsDashboard.dataState`
  localStorage.removeItem("guestCompanyId");
  localStorage.removeItem("pfToken");
  localStorage.removeItem("pensford");
  localStorage.removeItem("authRedirect");
  localStorage.removeItem("sisenseJWT");
  localStorage.removeItem("contactId");
  localStorage.removeItem("contactSettings");

  localStorage.removeItem("shouldEditDeal");
  localStorage.removeItem("apiV1Url");
  localStorage.removeItem("cache.BrokerDealsDashboard.dealData");
  localStorage.removeItem("contactSettings");

  Object.values(configItemsIds).forEach(val => {
    localStorage.removeItem(val);
  });

  localStorage.removeItem("loan_types");
  localStorage.removeItem("loan_seniority_types");
  localStorage.removeItem("loan_statuses");
  localStorage.removeItem("brokers");
  localStorage.removeItem("sales_brokers");
  localStorage.removeItem("deal_stages");
  localStorage.removeItem("deal_statuses");
  localStorage.removeItem("prepayment_types");
  localStorage.removeItem("fund_names");
  localStorage.removeItem("user_company_contacts");
  localStorage.removeItem("deal_company_contacts");

  trackLogout();

  const authUrl = yield call(getEnvConfig, "authUIUrl");
  window.location.assign(`${authUrl}${buildLoginPath(backTo ?? "")}`);
};

export const AUTH_SISENSE_LOGIN = generateDefaultActions("AUTH_SISENSE_LOGIN");
export const sisenseLogin = payload => makePayload(AUTH_SISENSE_LOGIN.value, payload);
function* sisenseLoginSaga() {
  const url = "/sisenseLogin";
  yield requestSaga(AUTH_SISENSE_LOGIN, url);
}

export const AUTH_SISENSE_LOGOUT = generateDefaultActions("AUTH_SISENSE_LOGOUT");
export const sisenseLogout = payload => makePayload(AUTH_SISENSE_LOGOUT.value, payload);
function* sisenseLogoutSaga() {
  const url = "/sisenseLogout";
  yield requestSaga(AUTH_SISENSE_LOGOUT, url);
}

export function* authSaga() {
  yield takeLatest(AUTH_LOGIN_CHECK_TOKEN, checkToken);
  yield takeLatest(AUTH_IS_APPS_ADMIN.value, isAppsAdminSaga);
  yield takeLatest(AUTH_LOGIN_REQUEST, login);
  yield takeLatest(AUTH_LOGOUT_REQUEST, logout);
  yield takeLatest(AUTH_SISENSE_LOGIN.value, sisenseLoginSaga);
  yield takeLatest(AUTH_SISENSE_LOGOUT.value, sisenseLogoutSaga);
  yield takeLatest(GET_COMPANY_LEVEL_BY_ID.value, getCompanyLevelByIdSaga);
  yield takeLatest(GET_COMPANY_TYPE_BY_ID.value, getCompanyTypeByIdSaga);
  yield takeLatest(REQUEST_SISENSE_JWT.value, requestSisenseJWTSaga);
}

// SELECTORS
const guestCompanyId = localStorage.getItem("guestCompanyId");
export const mapUserSelector = createSelector(state => state && state.user, value => value);
export const mapContactSelector = createSelector(mapUserSelector, value => value.contact);
export const mapCompanySelector = createSelector(mapContactSelector, value => value.company);
export const mapGuestPermissions = createSelector(state => state && state.user && state.user.contact && state.user.contact.guests && state.user.contact.guests.filter(x => x.companyId === guestCompanyId)[0], value => value);
export const mapPermissions = createSelector(state => state, state => ({
  roles: state.user.roles,
  isSystemAdmin: state.isSystemAdmin,
  isCompanyAdmin: state.isCompanyAdmin,
  companyLevel: state.companyLevel,
  companyType: state.companyType,
  isGuest: state.isGuest,
  guestAccess: state.guestAccess,
  guestPermissions: mapGuestPermissions(state)
}));
